/**
 * Whether this exploration's prompt cache is working, and if not, which of the
 * two reasons it is not.
 *
 * Workstream 6 of `docs/token-efficiency-implementation.md` asks for exactly one
 * thing before any cache-duration change may be considered: classify runs as
 * healthy, expiry-bound or prefix-unstable, off the workstream-1 metering. The
 * three lead to opposite actions — make no change, evaluate a one-hour TTL, or
 * fix the instability first and buy nothing — so a single "cache hit rate"
 * number would be the figure that cannot tell you what to do.
 *
 * The discriminator is the GAP, and it is the whole design. A cache write is not
 * itself a finding: the first pass on a prefix must write it, and a write after a
 * long pause is the provider's window doing what it says. A write after a SHORT
 * gap is the finding, because the entry that should have been read was still
 * live. So this counts writes on either side of that window rather than counting
 * writes — and the window is the one the pass's OWN provider publishes, since
 * five minutes and thirty are different questions asked of the same spacing.
 *
 * Nothing here reads a prefix, a digest or a cache key — the plan forbids logging
 * them and this module never has one. It reads token categories and two
 * timestamps, which is why it can be pure and why it cannot, on its own, tell a
 * legitimately changed prefix from an unstable one. `COHORT` below is how much of
 * that gap is closed, and the rest is stated rather than papered over.
 *
 * Nothing here spends, and nothing here changes a request. A classification is an
 * input to somebody's decision about a TTL, and the plan's six conditions for
 * buying one are not in this file because five of them are not facts about a
 * stored session.
 */

import type { Op, OpKind, Session } from './types'
import type { ProviderId } from './models'
import { CACHE_WRITE_CLASS } from './models'
import { cacheFacts } from './ledger'

/**
 * How long a cache entry lives, by provider, or null where nobody has published
 * a contract.
 *
 * Read off `CACHE_WRITE_CLASS` rather than restated, because that table is
 * already the app's answer to which short-lived cache each provider writes when
 * this app names no longer TTL — Anthropic five minutes from a bare
 * `{ type: 'ephemeral' }`, GPT-5.6 thirty from its own default. Two copies of
 * that fact would let a classification measure against a window the biller does
 * not use.
 *
 * This was one Anthropic constant applied to every pass, which reversed the
 * reading for OpenAI: a rewrite ten minutes after the previous pass is a live
 * entry that went unread — instability — and against five minutes it read as the
 * window expiring, which is the reading that recommends BUYING a longer one.
 *
 * `custom` is null and that is not a gap to fill: a user-supplied endpoint has no
 * published cache contract, so there is no window to measure against and a
 * default would be this file inventing one. Those passes are counted in the read
 * share and are not opportunities.
 */
export const CACHE_WINDOW_MS: Readonly<Record<ProviderId, number | null>> = {
  anthropic: CACHE_WRITE_CLASS.anthropic === '5m' ? 5 * 60_000 : null,
  openai: CACHE_WRITE_CLASS.openai === '30m' ? 30 * 60_000 : null,
  // Gemini's implicit cache publishes no window this app could measure a reuse
  // against, and it charges nothing to write — see `CACHE_WRITE_CLASS`.
  google: null,
  custom: null,
  // The judge writes no cache — see `CACHE_WRITE_CLASS` — so, like `custom`, its
  // rows have no window to be measured against and are never opportunities.
  typesafe: null,
}

/**
 * Opportunities a cohort needs before this will classify anything.
 *
 * Two is not enough: one write and one read is a hit rate of 50% and a sample of
 * one opportunity, and a cache that fired once tells you nothing about a run.
 *
 * An OPPORTUNITY is narrower than "a pass after the first". It is a pass with an
 * earlier pass on the same model, that touched the cache at all, under a provider
 * whose window is published — the three things that have to hold before a missed
 * read means anything. See the loop in `classifyCache` for why each is there.
 */
export const MIN_OPPORTUNITIES = 2

/**
 * The share of measured input that has to arrive from cache for a cohort to read
 * as healthy.
 *
 * The plan's words are "the stable prefix is mostly cache reads and writes are
 * rare", and "mostly" is the half this makes exact. Set at a half rather than
 * higher because the first pass on any prefix must write it, so a short run can
 * never approach a share the arithmetic denies it.
 */
export const HEALTHY_READ_SHARE = 0.5

/** The op kinds that send the derive system prefix — the one workstream 6 names.
 *
 *  `buildSystem(lens, temper, frame, brief)` is what all of these share, and it
 *  takes no target and no operation, which is the property the prompt-layer
 *  regression test pins. `verify`, `panel`, `audit` and `link` build their own
 *  system blocks from other builders and are deliberately out: pooling them would
 *  average four different prefixes into one hit rate and call it a cache. */
export const DERIVE_PREFIX_OPS: readonly OpKind[] = [
  'expand',
  'deepen',
  'challenge',
  'diverge',
  'experiment',
  'invert',
  'bridge',
  'revise',
  'outcome',
  // Re-derives from a check's result, but through `runOp`: the derive prefix, not the check's.
  'rebase',
  // Sends `buildSystem` unchanged — its brief rides the user turn — so it reads
  // and writes the same cached prefix as every verb above.
  'conjecture',
  'portfolio',
]

export type CacheReading =
  /** Mostly reads. Make no change. */
  | 'healthy'
  /** Writes, and they follow gaps longer than the window. A longer TTL is the
   *  thing worth evaluating — under the plan's six conditions, none of which
   *  this file checks. */
  | 'expiry-bound'
  /** Writes despite short gaps. Something is changing the prefix, and buying a
   *  longer TTL would pay more per write for writes that should not happen. */
  | 'prefix-unstable'
  /** Nothing to classify. Never a synonym for healthy. */
  | 'not-evidenced'

export interface CacheClassification {
  reading: CacheReading
  /**
   * The rule that fired and the thresholds it used, as a sentence.
   *
   * The same contract `core/cues.ts` holds itself to: a classification that
   * cannot say why it fired is a verdict, and this one is about to be an argument
   * for or against spending more per token. Every number in the sentence comes
   * from a named constant above.
   */
  rule: string
  /** Passes whose receipt carried a breakdown this could read. */
  measured: number
  /** Of those, the ones that could have read a live entry — see
   *  `MIN_OPPORTUNITIES` for what that excludes. */
  opportunities: number
  /** Opportunities that wrote after a gap SHORTER than their provider's window. */
  wroteInsideWindow: number
  /** Opportunities that wrote after a gap longer than it. */
  wroteAfterWindow: number
  /** Cache reads as a share of measured input across the cohort, 0–1. */
  readShare: number
}

/** One pass, reduced to what this question needs. Exported for the callers that
 *  hold something other than an `Op` — an eval receipt, say — so they need not
 *  fabricate one. */
export interface CachePass {
  startedAt: number
  /** Absent when the tab died mid-pass; the gap is then measured from
   *  `startedAt`, which understates it and therefore leans towards calling a
   *  write unstable rather than expired. Leaning towards the finding that stops
   *  a purchase is the safe direction for a figure nobody has verified. */
  finishedAt?: number
  /** The model that ran it, so a routed pass does not count as a repeat of a
   *  differently-routed one. */
  modelId: string
  /** Whose cache contract this pass falls under. Read from the receipt's own
   *  stamp, never inferred from the model id. */
  provider: ProviderId
  cached: number
  written: number
  uncached: number
}

/**
 * The derive passes of one session, oldest first, as this module needs them.
 *
 * Ops with no breakdown are dropped rather than zero-filled — `cacheFacts`
 * already draws that line and returns null rather than a row of zeroes, because
 * "recorded before the meter existed" and "every category was zero" are opposite
 * facts.
 */
export function derivePasses(session: Session): CachePass[] {
  const out: CachePass[] = []
  for (const op of session.ops) {
    if (!DERIVE_PREFIX_OPS.includes(op.kind)) continue
    const pass = passOf(op)
    if (pass) out.push(pass)
  }
  return out.sort((a, b) => a.startedAt - b.startedAt)
}

const passOf = (op: Op): CachePass | null => {
  if (!op.spend) return null
  const facts = cacheFacts(op.spend)
  if (!facts) return null
  return {
    startedAt: op.startedAt,
    ...(op.finishedAt !== undefined ? { finishedAt: op.finishedAt } : {}),
    modelId: op.spend.model.id,
    provider: op.spend.model.provider,
    cached: facts.cached,
    written: facts.written,
    uncached: facts.uncached,
  }
}

/**
 * Classify a sequence of passes, oldest first.
 *
 * COHORT: passes are grouped by model, and only a pass with an earlier pass on
 * the same model is an opportunity. That is the one confound this can remove
 * from what a stored op carries — a routed pass shares no cache with the
 * selection's, so counting it as a missed read would report routing as
 * instability.
 *
 * The confound it CANNOT remove is stated rather than hidden: an op records no
 * effort and no lens, and both participate in the prefix. A user who changed
 * temper mid-exploration, or a pass at a different effort, writes legitimately
 * and is counted here as unstable. That is the direction this errs in on purpose
 * — `prefix-unstable` is the reading that tells somebody to buy nothing.
 */
export function classifyCache(passes: readonly CachePass[]): CacheClassification {
  const seen = new Map<string, number>()
  let measured = 0
  let opportunities = 0
  let wroteInsideWindow = 0
  let wroteAfterWindow = 0
  let cached = 0
  let total = 0

  for (const p of passes) {
    measured++
    cached += p.cached
    total += p.cached + p.written + p.uncached
    const window = CACHE_WINDOW_MS[p.provider]
    const prevEnd = seen.get(p.modelId)
    seen.set(p.modelId, p.finishedAt ?? p.startedAt)
    if (prevEnd === undefined) continue
    // Nothing was cached either way, so there was no entry to read and none to
    // rewrite. A prefix below the provider's cache minimum bills its whole input
    // uncached, and counting that as a rewrite put a fact about the PROMPT into a
    // figure about the cache — three short passes read as `prefix-unstable` over a
    // rule claiming a rewrite that never happened.
    if (p.cached === 0 && p.written === 0) continue
    // No published cache contract — see `CACHE_WINDOW_MS`. There is no window to
    // measure this against, so it is not an opportunity rather than a guess.
    if (window === null) continue
    opportunities++
    // A pass that read anything read the entry, whatever else it also wrote: a
    // partial read is the cache working on the part that was stable.
    if (p.cached > 0) continue
    if (p.startedAt - prevEnd > window) wroteAfterWindow++
    else wroteInsideWindow++
  }

  const readShare = total > 0 ? cached / total : 0
  const pct = (n: number) => `${Math.round(n * 100)}%`
  // Named for the sentence, and plural because every window this app measures
  // against is minutes. Taken over the providers actually in the cohort, so a
  // mixed-provider reading does not print one house's window over both.
  const windows = [...new Set(passes.map((p) => CACHE_WINDOW_MS[p.provider]))]
    .filter((w): w is number => w !== null)
    .sort((a, b) => a - b)
    .map((w) => `${w / 60_000} minutes`)
  const window = windows.length ? windows.join(' or ') : 'the provider window'

  if (opportunities < MIN_OPPORTUNITIES) {
    return {
      reading: 'not-evidenced',
      rule: `${opportunities} of the ${MIN_OPPORTUNITIES} repeat passes this needs, over ${measured} measured. Nothing is classified, which is not the same as a cache that is working.`,
      measured,
      opportunities,
      wroteInsideWindow,
      wroteAfterWindow,
      readShare,
    }
  }

  if (readShare >= HEALTHY_READ_SHARE) {
    return {
      reading: 'healthy',
      rule: `${pct(readShare)} of measured input arrived from cache, at or above the ${pct(HEALTHY_READ_SHARE)} this calls mostly. Make no change.`,
      measured,
      opportunities,
      wroteInsideWindow,
      wroteAfterWindow,
      readShare,
    }
  }

  if (wroteInsideWindow > wroteAfterWindow) {
    return {
      reading: 'prefix-unstable',
      rule: `${wroteInsideWindow} of ${opportunities} repeat passes wrote the cache again within ${window} of the previous pass on that model, against ${wroteAfterWindow} that wrote after longer than that. An entry that was still live was not read, so something is changing the prefix — a longer window would pay more per write for writes that should not be happening.`,
      measured,
      opportunities,
      wroteInsideWindow,
      wroteAfterWindow,
      readShare,
    }
  }

  if (wroteAfterWindow > wroteInsideWindow) {
    return {
      reading: 'expiry-bound',
      rule: `${wroteAfterWindow} of ${opportunities} repeat passes wrote the cache again after longer than ${window} since the previous pass on that model, against ${wroteInsideWindow} within it. The prefix is holding and the window is what is expiring.`,
      measured,
      opportunities,
      wroteInsideWindow,
      wroteAfterWindow,
      readShare,
    }
  }

  // Neither side predominates. This used to fall through to `expiry-bound`, which
  // meant a tie — one short-gap write against one long — and even a cohort with no
  // long-gap write at all asserted that the window was what expired. That is the
  // reading that recommends BUYING a longer TTL, so the tie was breaking towards a
  // purchase on evidence that names no cause.
  return {
    reading: 'not-evidenced',
    rule: `${wroteInsideWindow} rewrites within ${window} against ${wroteAfterWindow} after it, over ${opportunities} repeat passes — tied, so neither a changing prefix nor an expiring window is what the evidence names. Nothing is classified, which is not the same as a cache that is working.`,
    measured,
    opportunities,
    wroteInsideWindow,
    wroteAfterWindow,
    readShare,
  }
}

/** The whole question for one session, in one call. */
export const cacheHealthOf = (session: Session): CacheClassification =>
  classifyCache(derivePasses(session))
